import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import {
	activateScope as activateScopeAction,
	hydrateInsightScope,
	setAccount,
	setCloud,
	setMode,
	setRegion,
	setSelectedResource,
	setSource,
	type InsightActiveMode,
} from "@redux/slices/insightScopeSlice";
import type { InsightSourceType } from "../types/insightApi";

const PARAM_KEYS = {
	sourceType: "sourceType",
	sourceId: "sourceId",
	sourceName: "sourceName",
	cloud: "cloud",
	region: "region",
	account: "account",
	mode: "mode",
	resource: "resource",
} as const;

type ParamKey = keyof typeof PARAM_KEYS;

function parseSourceId(value: string | null): string | null {
	return value && value.trim() ? value : null;
}

/**
 * Bridges the shared `insightScope` redux slice (see `insightScopeSlice.ts`)
 * with the URL querystring so a refreshed or shared deep link restores
 * source type, authorized source, cloud, region, account, active mode and
 * selected resource — the CTX-97 acceptance requirement for deep-link
 * restoration.
 *
 * The URL is treated as the actual source of truth on first mount: whatever
 * `sourceId` is in the querystring gets hydrated into redux as-is, untrusted
 * — `InsightTopologyViewV2` then asks the backend for that source's scopes,
 * and a foreign/garbage id simply produces the backend's 404, rendered as
 * the "unauthorized/not-found" status instead of a client-side guess. Every
 * `select*` setter below writes to both redux and the URL in the same call
 * so the two can never drift apart.
 */
export function useInsightScopeUrlSync() {
	const [searchParams, setSearchParams] = useSearchParams();
	const dispatch = useAppDispatch();
	const scope = useAppSelector((state) => state.insightScope);
	const hydratedRef = useRef(false);

	useEffect(() => {
		if (hydratedRef.current) return;
		hydratedRef.current = true;

		const sourceType = searchParams.get(PARAM_KEYS.sourceType) as InsightSourceType | null;
		const sourceId = parseSourceId(searchParams.get(PARAM_KEYS.sourceId));
		if (!sourceType || sourceId == null) return;
		if (sourceType !== "reflector" && sourceType !== "cluster") return;

		dispatch(
			hydrateInsightScope({
				sourceType,
				sourceId,
				sourceName: searchParams.get(PARAM_KEYS.sourceName) || "",
				cloud: searchParams.get(PARAM_KEYS.cloud),
				region: searchParams.get(PARAM_KEYS.region),
				account: searchParams.get(PARAM_KEYS.account),
				mode: (searchParams.get(PARAM_KEYS.mode) as InsightActiveMode) || "topology",
				selectedResourceId: searchParams.get(PARAM_KEYS.resource),
			})
		);
		// Deliberately run once on mount only — subsequent URL writes below
		// originate from these same setters, not from outside navigation.
	}, []);

	const writeParams = useCallback(
		(patch: Partial<Record<ParamKey, string | null>>) => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					(Object.keys(patch) as ParamKey[]).forEach((key) => {
						const value = patch[key];
						const paramName = PARAM_KEYS[key];
						if (value == null || value === "") {
							next.delete(paramName);
						} else {
							next.set(paramName, value);
						}
					});
					return next;
				},
				{ replace: true }
			);
		},
		[setSearchParams]
	);

	const selectSource = useCallback(
		(sourceType: InsightSourceType, sourceId: string, sourceName: string) => {
			dispatch(setSource({ sourceType, sourceId, sourceName }));
			writeParams({
				sourceType,
				sourceId,
				sourceName,
				cloud: null,
				region: null,
				account: null,
				resource: null,
			});
		},
		[dispatch, writeParams]
	);

	const selectCloud = useCallback(
		(cloud: string | null) => {
			dispatch(setCloud(cloud));
			writeParams({ cloud, region: null, account: null, resource: null });
		},
		[dispatch, writeParams]
	);

	const selectRegion = useCallback(
		(region: string | null) => {
			dispatch(setRegion(region));
			writeParams({ region, account: null, resource: null });
		},
		[dispatch, writeParams]
	);

	const selectAccount = useCallback(
		(account: string | null) => {
			dispatch(setAccount(account));
			writeParams({ account, resource: null });
		},
		[dispatch, writeParams]
	);

	const selectMode = useCallback(
		(mode: InsightActiveMode) => {
			dispatch(setMode(mode));
			writeParams({ mode });
		},
		[dispatch, writeParams]
	);

	const selectResource = useCallback(
		(resourceId: string | null) => {
			dispatch(setSelectedResource(resourceId));
			writeParams({ resource: resourceId });
		},
		[dispatch, writeParams]
	);

	/**
	 * Activates a whole Reflector/Cluster -> Cloud -> Region -> Account path in
	 * one dispatch + one URL write, used by the hierarchical scope browser
	 * (CTX-97 UX rework) when a leaf node is clicked — avoids the 4 separate
	 * `select*` writes racing each other / producing intermediate URL states.
	 */
	const activateScope = useCallback(
		(
			sourceType: InsightSourceType,
			sourceId: string,
			sourceName: string,
			cloud: string | null,
			region: string | null,
			account: string | null
		) => {
			dispatch(activateScopeAction({ sourceType, sourceId, sourceName, cloud, region, account }));
			writeParams({
				sourceType,
				sourceId,
				sourceName,
				cloud,
				region,
				account,
				resource: null,
			});
		},
		[dispatch, writeParams]
	);

	return {
		scope,
		selectSource,
		selectCloud,
		selectRegion,
		selectAccount,
		selectMode,
		selectResource,
		activateScope,
	};
}
